import { useEffect, useMemo, useState } from "react"
import { CircularProgress, Grid, Typography } from "src/lib"
import intl from "src/internationalization/intl"
import { listRankingsPerMarket, type RankingPerMarketItem } from "src/services/api/sorting"
import { useConfirmedRankingStore } from "src/store/confirmedRankingStore"
import { getMarketRankingDisplayName, RANKING_MARKET_KEYS_ORDER } from "./useRankingAssets"

function pickLatestPerMarket(rows: RankingPerMarketItem[]): Map<string, RankingPerMarketItem> {
    const map = new Map<string, RankingPerMarketItem>()
    for (const r of rows) {
        const prev = map.get(r.market)
        if (!prev) {
            map.set(r.market, r)
            continue
        }
        const prevT = Date.parse(prev.updatedAt || prev.createdAt) || 0
        const nextT = Date.parse(r.updatedAt || r.createdAt) || 0
        if (nextT >= prevT) map.set(r.market, r)
    }
    return map
}

const AdvisorRankingsPreview = () => {
    const mergeConfirmedRankingsFromServer = useConfirmedRankingStore((s) => s.mergeConfirmedRankingsFromServer)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const [byMarket, setByMarket] = useState<Map<string, RankingPerMarketItem>>(() => new Map())

    useEffect(() => {
        let cancelled = false
        const run = async () => {
            setLoading(true)
            setError(null)
            try {
                const { rankings } = await listRankingsPerMarket()
                if (cancelled) return
                const picked = pickLatestPerMarket(rankings)
                setByMarket(picked)
                mergeConfirmedRankingsFromServer(
                    Array.from(picked.values()).map((r) => ({
                        market: r.market,
                        ranking_text: r.rankingText,
                        cypher_query: r.cypherQuery,
                    }))
                )
            } catch {
                if (!cancelled) setError(intl("rankingPreviewLoadError"))
            } finally {
                if (!cancelled) setLoading(false)
            }
        }
        void run()
        return () => {
            cancelled = true
        }
    }, [mergeConfirmedRankingsFromServer])

    const rows = useMemo(
        () =>
            RANKING_MARKET_KEYS_ORDER.map((market) => ({
                market,
                label: getMarketRankingDisplayName(market),
                row: byMarket.get(market),
            })),
        [byMarket]
    )

    if (loading) {
        return (
            <Grid container justifyContent="center" sx={{ py: 2 }}>
                <CircularProgress size={28} color="primary" />
            </Grid>
        )
    }

    if (error) {
        return (
            <Typography variant="body2" color="secondary" sx={{ mt: 1 }}>
                {error}
            </Typography>
        )
    }

    return (
        <Grid container spacing={1.5} sx={{ mt: 1 }}>
            {rows.map(({ market, label, row }) => {
                const criterion = row?.rankingText?.trim()
                const isFundos = market === "FUNDOS"
                const meta =
                    row?.lastEditedByName?.trim() && (row.updatedAt || row.createdAt)
                        ? intl("rankingPreviewEditedMeta", {
                              values: {
                                  name: row.lastEditedByName.trim(),
                                  date: (row.updatedAt || row.createdAt).split("T")[0] ?? "",
                              },
                          })
                        : null
                return (
                    <Grid item xs={12} sm={6} md={3} key={market}>
                        <Grid
                            sx={{
                                border: 1,
                                borderColor: "divider",
                                borderRadius: 1,
                                p: 1.25,
                                height: "100%",
                                bgcolor: "background.paper",
                            }}
                        >
                            <Typography
                                variant="subtitle2"
                                component="p"
                                sx={{ fontWeight: 700, lineHeight: 1.3, color: "text.primary" }}
                            >
                                {label}
                            </Typography>
                            <Typography
                                variant="caption"
                                component="p"
                                sx={{
                                    mt: 0.25,
                                    display: "block",
                                    fontWeight: 600,
                                    letterSpacing: "0.04em",
                                    color: "primary.main",
                                    textTransform: "uppercase",
                                }}
                            >
                                {market}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    mt: 0.75,
                                    lineHeight: 1.35,
                                    fontWeight: !criterion && isFundos ? 600 : 400,
                                    color: !criterion && isFundos ? "primary.main" : "text.secondary",
                                }}
                            >
                                {criterion ||
                                    (isFundos
                                        ? intl("rankingExampleCriterion")
                                        : intl("rankingPreviewNotConfigured"))}
                            </Typography>
                            {meta ? (
                                <Typography variant="caption" color="secondary" sx={{ mt: 0.75, display: "block" }}>
                                    {meta}
                                </Typography>
                            ) : null}
                        </Grid>
                    </Grid>
                )
            })}
        </Grid>
    )
}

export { AdvisorRankingsPreview }
